import { createTheme } from '@mui/material/styles';

// Shared typography and shape settings
const sharedTypography = {
  fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em' },
  h2: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em' },
  h3: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em' },
  h4: { fontSize: '1.25rem', fontWeight: 600 },
  h5: { fontSize: '1.125rem', fontWeight: 600 },
  h6: { fontSize: '1rem', fontWeight: 600 },
  body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
  body2: { fontSize: '0.875rem', lineHeight: 1.5 },
  button: { textTransform: 'none', fontWeight: 600 },
};

const sharedShape = { borderRadius: 12 };

// ==================== LIGHT THEME ====================
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ec4899',
      light: '#f472b6',
      dark: '#db2777',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    success: { main: '#10b981', light: '#34d399', dark: '#059669' },
    warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
    error: { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
    info: { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb' },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, padding: '10px 20px', fontSize: '0.9375rem' },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)' },
        },
        outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#f8fafc',
            '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.15)' },
            '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.3)' },
            '&.Mui-focused fieldset': { borderColor: '#6366f1' },
          },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: 'rgba(0, 0, 0, 0.08)' },
        head: { fontWeight: 600, backgroundColor: '#f8fafc' },
      },
    },
    MuiTableRow: {
      styleOverrides: { root: { '&:hover': { backgroundColor: '#f1f5f9' } } },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#ffffff', borderRight: '1px solid rgba(0, 0, 0, 0.08)' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          color: '#1e293b',
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { backgroundColor: '#ffffff', backgroundImage: 'none' } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiSelect: { styleOverrides: { root: { borderRadius: 10 } } },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, minWidth: 'auto', padding: '12px 20px' },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { height: 3, borderRadius: 2 } } },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-cell': { borderBottomColor: 'rgba(0, 0, 0, 0.08)' },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f8fafc',
            borderBottomColor: 'rgba(0, 0, 0, 0.08)',
          },
          '& .MuiDataGrid-footerContainer': { borderTopColor: 'rgba(0, 0, 0, 0.08)' },
        },
      },
    },
  },
});

// ==================== MEDIUM THEME (Soft Gray) ====================
const mediumTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ec4899',
      light: '#f472b6',
      dark: '#db2777',
      contrastText: '#ffffff',
    },
    background: {
      default: '#e2e8f0',
      paper: '#f1f5f9',
    },
    text: {
      primary: '#1e293b',
      secondary: '#475569',
    },
    success: { main: '#10b981', light: '#34d399', dark: '#059669' },
    warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
    error: { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
    info: { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb' },
    divider: 'rgba(0, 0, 0, 0.1)',
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, padding: '10px 20px', fontSize: '0.9375rem' },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)' },
        },
        outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#f8fafc',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#ffffff',
            '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.15)' },
            '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.3)' },
            '&.Mui-focused fieldset': { borderColor: '#6366f1' },
          },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: 'rgba(0, 0, 0, 0.1)' },
        head: { fontWeight: 600, backgroundColor: '#e2e8f0' },
      },
    },
    MuiTableRow: {
      styleOverrides: { root: { '&:hover': { backgroundColor: '#e2e8f0' } } },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#f1f5f9', borderRight: '1px solid rgba(0, 0, 0, 0.1)' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#f1f5f9',
          backgroundImage: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          color: '#1e293b',
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { backgroundColor: '#f8fafc', backgroundImage: 'none' } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#f8fafc',
          backgroundImage: 'none',
          border: '1px solid rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiSelect: { styleOverrides: { root: { borderRadius: 10 } } },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, minWidth: 'auto', padding: '12px 20px' },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { height: 3, borderRadius: 2 } } },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-cell': { borderBottomColor: 'rgba(0, 0, 0, 0.1)' },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#e2e8f0',
            borderBottomColor: 'rgba(0, 0, 0, 0.1)',
          },
          '& .MuiDataGrid-footerContainer': { borderTopColor: 'rgba(0, 0, 0, 0.1)' },
        },
      },
    },
  },
});

// ==================== DARK THEME ====================
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ec4899',
      light: '#f472b6',
      dark: '#db2777',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0f0f1a',
      paper: '#1a1a2e',
    },
    text: {
      primary: '#e4e4e7',
      secondary: '#a1a1aa',
    },
    success: { main: '#10b981', light: '#34d399', dark: '#059669' },
    warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
    error: { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
    info: { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb' },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: sharedTypography,
  shape: sharedShape,
  shadows: [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.3)',
    '0 2px 4px rgba(0, 0, 0, 0.3)',
    '0 4px 8px rgba(0, 0, 0, 0.3)',
    '0 6px 12px rgba(0, 0, 0, 0.3)',
    '0 8px 16px rgba(0, 0, 0, 0.3)',
    '0 10px 20px rgba(0, 0, 0, 0.3)',
    '0 12px 24px rgba(0, 0, 0, 0.3)',
    '0 14px 28px rgba(0, 0, 0, 0.3)',
    '0 16px 32px rgba(0, 0, 0, 0.3)',
    '0 18px 36px rgba(0, 0, 0, 0.3)',
    '0 20px 40px rgba(0, 0, 0, 0.3)',
    '0 22px 44px rgba(0, 0, 0, 0.3)',
    '0 24px 48px rgba(0, 0, 0, 0.3)',
    '0 26px 52px rgba(0, 0, 0, 0.3)',
    '0 28px 56px rgba(0, 0, 0, 0.3)',
    '0 30px 60px rgba(0, 0, 0, 0.3)',
    '0 32px 64px rgba(0, 0, 0, 0.3)',
    '0 34px 68px rgba(0, 0, 0, 0.3)',
    '0 36px 72px rgba(0, 0, 0, 0.3)',
    '0 38px 76px rgba(0, 0, 0, 0.3)',
    '0 40px 80px rgba(0, 0, 0, 0.3)',
    '0 42px 84px rgba(0, 0, 0, 0.3)',
    '0 44px 88px rgba(0, 0, 0, 0.3)',
    '0 46px 92px rgba(0, 0, 0, 0.3)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, padding: '10px 20px', fontSize: '0.9375rem' },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)' },
        },
        outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1a1a2e',
          border: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
            '&.Mui-focused fieldset': { borderColor: '#6366f1' },
          },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: 'rgba(255, 255, 255, 0.06)' },
        head: { fontWeight: 600, backgroundColor: 'rgba(255, 255, 255, 0.03)' },
      },
    },
    MuiTableRow: {
      styleOverrides: { root: { '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' } } },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#1a1a2e', borderRight: '1px solid rgba(255, 255, 255, 0.06)' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a2e',
          backgroundImage: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { backgroundColor: '#1a1a2e', backgroundImage: 'none' } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1a1a2e',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },
    MuiSelect: { styleOverrides: { root: { borderRadius: 10 } } },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, minWidth: 'auto', padding: '12px 20px' },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { height: 3, borderRadius: 2 } } },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-cell': { borderBottomColor: 'rgba(255, 255, 255, 0.06)' },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderBottomColor: 'rgba(255, 255, 255, 0.06)',
          },
          '& .MuiDataGrid-footerContainer': { borderTopColor: 'rgba(255, 255, 255, 0.06)' },
        },
      },
    },
  },
});

// ==================== EXTENDED TEMPLATES (forks of light/dark) ====================
const oceanTheme = createTheme(lightTheme, {
  palette: {
    primary: { main: '#0d9488', light: '#14b8a6', dark: '#0f766e', contrastText: '#ffffff' },
    secondary: { main: '#ea580c', light: '#fb923c', dark: '#c2410c', contrastText: '#ffffff' },
    background: { default: '#ecfeff', paper: '#f0fdfa' },
    divider: 'rgba(13, 148, 136, 0.12)',
  },
});

const roseTheme = createTheme(lightTheme, {
  palette: {
    primary: { main: '#e11d48', light: '#fb7185', dark: '#be123c', contrastText: '#ffffff' },
    secondary: { main: '#7c3aed', light: '#a78bfa', dark: '#5b21b6', contrastText: '#ffffff' },
    background: { default: '#fff1f2', paper: '#ffffff' },
    divider: 'rgba(225, 29, 72, 0.1)',
  },
});

const graphiteTheme = createTheme(lightTheme, {
  palette: {
    primary: { main: '#475569', light: '#64748b', dark: '#334155', contrastText: '#ffffff' },
    secondary: { main: '#6366f1', light: '#818cf8', dark: '#4f46e5', contrastText: '#ffffff' },
    background: { default: '#e2e8f0', paper: '#f8fafc' },
    text: { primary: '#0f172a', secondary: '#475569' },
    divider: 'rgba(15, 23, 42, 0.1)',
  },
});

/** Labels for the appearance picker (ids must match `themes` keys). */
export const themeTemplateOptions = [
  { id: 'light', label: 'Light', hint: 'Bright default' },
  { id: 'medium', label: 'Soft gray', hint: 'Calmer neutrals' },
  { id: 'dark', label: 'Dark', hint: 'Low-light UI' },
  { id: 'ocean', label: 'Ocean', hint: 'Teal & amber accents' },
  { id: 'rose', label: 'Rose', hint: 'Rose & violet accents' },
  { id: 'graphite', label: 'Graphite', hint: 'Slate professional' },
];

// Export all themes
export const themes = {
  light: lightTheme,
  medium: mediumTheme,
  dark: darkTheme,
  ocean: oceanTheme,
  rose: roseTheme,
  graphite: graphiteTheme,
};

// Default export for backwards compatibility
export default darkTheme;
